<template>
  <div>
    <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
      <h2 class="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">
        Geautoriseerde Gebruikers
      </h2>
      <button
        @click="openAddModal"
        class="bg-blue-600 hover:bg-blue-700 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center gap-2 transition-colors text-sm"
      >
        <PlusIcon class="h-4 w-4" />
        <span class="sm:inline">Toevoegen</span>
      </button>
    </div>

    <!-- Loading state -->
    <div v-if="isLoadingUsers" class="text-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      <p class="mt-4 text-gray-600 dark:text-gray-400">Laden...</p>
    </div>

    <!-- Error state -->
    <div
      v-else-if="usersError"
      class="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
    >
      <p class="text-red-800 dark:text-red-300">
        Fout bij laden van gebruikers: {{ usersError.message }}
      </p>
    </div>

    <!-- Users Table -->
    <UserTable
      v-else
      :users="users || []"
      :current-user-email="currentUser?.email"
      :available-roles="availableRoles"
      @edit="openEditModal"
      @delete="confirmDelete"
    />

    <!-- User Form Modal -->
    <UserFormModal
      :open="showUserModal"
      :user="userToEdit"
      :available-roles="availableRoles"
      @close="closeUserModal"
      @save="handleSaveUser"
    />

    <!-- User Delete Modal -->
    <UserDeleteModal
      :open="showDeleteModal"
      :user="userToDelete"
      :is-deleting="isDeleting"
      @close="showDeleteModal = false"
      @confirm="handleDeleteUser"
    />
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { PlusIcon } from '@heroicons/vue/24/outline'
import { authorizedUsersService } from '@/services/adminService.js'
import { useAuth } from '@/composables/auth/useAuth'

import UserTable from '@/components/admin/UserManagement/UserTable.vue'
import UserFormModal from '@/components/admin/UserManagement/UserFormModal.vue'
import UserDeleteModal from '@/components/admin/UserManagement/UserDeleteModal.vue'

const { user: currentUser } = useAuth()
const queryClient = useQueryClient()

const availableRoles = [
  { value: 'admin', label: 'Admin', color: 'bg-purple-100 text-purple-700' },
  { value: 'commerciant', label: 'Commerciant', color: 'bg-blue-100 text-blue-700' },
  { value: 'performance_lead', label: 'Performance Lead', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'sourcingsbaas', label: 'Sourcingsbaas', color: 'bg-teal-100 text-teal-700' },
  { value: 'user', label: 'Gebruiker', color: 'bg-gray-100 text-gray-700' }
]

const showUserModal = ref(false)
const showDeleteModal = ref(false)
const userToEdit = ref(null)
const userToDelete = ref(null)
const isDeleting = ref(false)

// Fetch users
const {
  data: users,
  isLoading: isLoadingUsers,
  error: usersError,
  refetch: refetchUsers
} = useQuery({
  queryKey: ['authorized-users'],
  queryFn: () => authorizedUsersService.getAll(),
  staleTime: 1000 * 60 * 5
})

const openAddModal = () => {
  userToEdit.value = null
  showUserModal.value = true
}

const openEditModal = user => {
  userToEdit.value = user
  showUserModal.value = true
}

const closeUserModal = () => {
  showUserModal.value = false
  userToEdit.value = null
}

const handleSaveUser = async formData => {
  try {
    const payload = {
      name: formData.name,
      role: formData.role,
      is_active: formData.is_active,
      dashboard_access: formData.dashboard_access,
      team_ids: formData.team_ids
    }

    if (formData.isEditing) {
      await authorizedUsersService.update(formData.id, payload)
    } else {
      await authorizedUsersService.create({
        email: formData.email,
        ...payload
      })
    }
    await refetchUsers()
    // Refresh team-PL overview when team assignments may have changed
    queryClient.invalidateQueries({ queryKey: ['team-pl-overview'] })
    closeUserModal()
  } catch (err) {
    throw new Error(err.response?.data?.error || 'Fout bij opslaan van gebruiker')
  }
}

const confirmDelete = user => {
  if (user.email === currentUser.value?.email) {
    return // Can't delete yourself
  }
  userToDelete.value = user
  showDeleteModal.value = true
}

const handleDeleteUser = async () => {
  if (!userToDelete.value) return

  isDeleting.value = true
  try {
    await authorizedUsersService.delete(userToDelete.value.id)
    await refetchUsers()
    showDeleteModal.value = false
    userToDelete.value = null
  } catch (err) {
    console.error('Error deleting user:', err)
    alert('Fout bij verwijderen van gebruiker')
  } finally {
    isDeleting.value = false
  }
}
</script>
